import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, srcForAudio } from "@/lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const listenedMarked = useRef(false);

  if (!audioRef.current) {
    audioRef.current = new Audio();
  }

  const markListened = useCallback((episodeId) => {
    if (listenedMarked.current) return;
    listenedMarked.current = true;
    api.post(`/episodes/${episodeId}/listen`).catch(() => {});
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    const onTime = () => {
      setProgress(audio.currentTime);
      if (current && audio.duration && audio.currentTime / audio.duration > 0.5) {
        markListened(current.id);
      }
    };
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      if (current) markListened(current.id);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
  }, [current, markListened]);

  const play = useCallback((episode) => {
    const audio = audioRef.current;
    if (current && current.id === episode.id) {
      audio.play();
      setPlaying(true);
      return;
    }
    listenedMarked.current = false;
    audio.src = srcForAudio(episode);
    audio.volume = volume;
    setCurrent(episode);
    setProgress(0);
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [current, volume]);

  const pause = useCallback(() => {
    audioRef.current.pause();
    setPlaying(false);
  }, []);

  const load = useCallback((episode) => {
    if (!episode || (current && current.id === episode.id)) return;
    const audio = audioRef.current;
    listenedMarked.current = false;
    audio.src = srcForAudio(episode);
    audio.volume = volume;
    setCurrent(episode);
    setProgress(0);
    setPlaying(false);
  }, [current, volume]);

  const toggle = useCallback((episode) => {
    if (episode && (!current || current.id !== episode.id)) {
      play(episode);
    } else if (playing) {
      pause();
    } else if (current) {
      audioRef.current.play();
      setPlaying(true);
    }
  }, [current, playing, play, pause]);

  const seek = useCallback((t) => {
    audioRef.current.currentTime = t;
    setProgress(t);
  }, []);

  const changeVolume = useCallback((v) => {
    audioRef.current.volume = v;
    setVolume(v);
  }, []);

  return (
    <PlayerContext.Provider value={{ current, playing, progress, duration, volume, play, pause, toggle, seek, changeVolume, load }}>
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  return useContext(PlayerContext);
}
